import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
    Play, Pause, Volume2, VolumeX, Maximize2, ArrowLeft,
    RefreshCw, Flag, Share2, Clock, AlertTriangle,
    Eye, Crown // <--- ADDED CROWN IMPORT
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FONTS_MAP = {
    'Roboto': 'font-roboto',
    'Oswald': 'font-heading',
    'Poppins': 'font-body',
    'Bangers': 'font-comic',
    'Lora': 'font-lora',
    'Courier Prime': 'font-mono',
    'Permanent Marker': 'font-handwritten',
    'Anton': 'font-brand',
    'Nunito': 'font-nunito',
    'Montserrat': 'font-montserrat',
};

const PRESETS = {
    'standard': { font: 'Roboto', color: '#FFFFFF' },
    'meme': { font: 'Oswald', color: '#FFFF00' },
    'cinema': { font: 'Lora', color: '#FFFFFF' },
};

const SIZE_MAP = {
    'small': 'text-lg md:text-xl',
    'medium': 'text-xl md:text-2xl',
    'large': 'text-2xl md:text-3xl',
    'huge': 'text-3xl md:text-4xl',
};

const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default function Watch() {
    const { shareId } = useParams();
    const navigate = useNavigate();
    const { isPro: viewerIsPro } = useAuth();
    const videoRef = useRef(null);
    const containerRef = useRef(null);

    // State
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Player state
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showControls, setShowControls] = useState(true);

    // Ad/Overlay state
    const [showAdOverlay, setShowAdOverlay] = useState(true);
    const [adCountdown, setAdCountdown] = useState(5);
    const [isEmbedded, setIsEmbedded] = useState(false);
    const [showExpiredOverlay, setShowExpiredOverlay] = useState(false);
    const [showUnmuteButton, setShowUnmuteButton] = useState(false);

    // Report dialog
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [reporting, setReporting] = useState(false);

    const handleBack = () => {
        // React Router v6 adds an 'idx' to the history state. 
        // If idx > 0, the user has navigated within the app.
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            // No app history (direct link from outside), send to Gallery/Home
            navigate('/'); 
        }
    };

    useEffect(() => {
        const inIframe = window.self !== window.top;
        setIsEmbedded(inIframe);
        if (inIframe) {
            setShowAdOverlay(false);
        }
    }, []);

    useEffect(() => {
        const fetchSession = async () => {
            try {
                const response = await axios.get(`${API}/session/${shareId}`);
                setSession(response.data);

                axios.post(`${API}/session/${shareId}/view`)
                    .catch(err => console.log("View count skipped:", err));

                if (response.data.expires_at) {
                    const expiryDate = new Date(response.data.expires_at);
                    if (expiryDate < new Date() && !response.data.creator_is_pro) {
                        setShowExpiredOverlay(true);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch session:', err);
                setError('This clip could not be found.');
            } finally {
                setLoading(false);
            }
        };

        if (shareId) {
            fetchSession();
        }
    }, [shareId]);

    useEffect(() => {
        if (showAdOverlay && adCountdown > 0) {
            const timer = setTimeout(() => setAdCountdown(adCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [showAdOverlay, adCountdown]);

    useEffect(() => {
        let timeout;
        if (isPlaying && showControls) {
            timeout = setTimeout(() => setShowControls(false), 3000);
        }
        return () => clearTimeout(timeout);
    }, [isPlaying, showControls]);

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play().catch(() => {
                    setShowUnmuteButton(true);
                });
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        const newTime = pos * duration;
        if (videoRef.current) {
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const handleUnmute = () => {
        setIsMuted(false);
        setShowUnmuteButton(false);
        if (videoRef.current) {
            videoRef.current.muted = false;
            videoRef.current.play();
        }
    };

    const handleFullscreen = () => {
        if (containerRef.current) {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else {
                containerRef.current.requestFullscreen();
            }
        }
    };

    const skipAd = () => {
        setShowAdOverlay(false);
        if (videoRef.current) {
            videoRef.current.play().catch(() => {
                setShowUnmuteButton(true);
            });
            setIsPlaying(true);
        }
    };

    const handleReport = async () => {
        if (!reportReason.trim()) {
            toast.error('Please provide a reason');
            return;
        }

        setReporting(true);
        try {
            await axios.post(`${API}/report`, {
                share_session_id: shareId,
                reason: reportReason,
            });
            toast.success('Report submitted. Thank you for helping keep SubHack safe.');
            setShowReportDialog(false);
            setReportReason('');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to submit report';
            toast.error(message);
        } finally {
            setReporting(false);
        }
    };

    const getCurrentSubtitle = () => {
        if (!session?.subtitle_data) return null;
        return session.subtitle_data.find(sub =>
            currentTime >= sub.start_time && currentTime <= sub.end_time
        );
    };

    const getSubtitleStyle = () => {
        const preset = PRESETS[session?.style_preset] || PRESETS.standard;
        const customStyle = session?.custom_style;

        let fontFamily = preset.font;
        let color = preset.color;
        let sizeClass = 'text-xl md:text-2xl';

        if (session?.style_preset === 'custom' && customStyle) {
            fontFamily = customStyle.font_family || 'Roboto';
            color = customStyle.text_color || '#FFFFFF';
            sizeClass = SIZE_MAP[customStyle.font_size] || SIZE_MAP.medium;
        }

        const fontClass = FONTS_MAP[fontFamily] || 'font-roboto';

        return { fontClass, color, sizeClass };
    };

    if (loading) {
        return (
            <div className="h-[100dvh] bg-black flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading clip...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-[100dvh] bg-black flex items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="w-16 h-16 text-destructive mx-auto mb-4" />
                    <h1 className="font-heading text-2xl uppercase mb-2">Not Found</h1>
                    <p className="text-muted-foreground mb-6">{error}</p>
                    <Button className="btn-primary" onClick={() => navigate('/')}>
                        Go Home
                    </Button>
                </div>
            </div>
        );
    }

    const currentSub = getCurrentSubtitle();
    const subtitleStyle = getSubtitleStyle();

    const showWatermark = !viewerIsPro && (isEmbedded || !session?.creator_is_pro);

    const firstSubtitleRaw = session?.subtitle_data?.[0]?.text || '';
    const firstSubtitle = firstSubtitleRaw.replace(/<[^>]+>/g, '');
    const ogDescription = firstSubtitle
        ? `"${firstSubtitle}" - Watch this clip on SubHack`
        : 'Watch this subtitled clip on SubHack';
    const ogImage = session?.clip?.thumbnail_url || 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=1200';
    const ogTitle = session?.title || 'SubHack Clip';

    return (
        <div
            ref={containerRef}
            className="h-[100dvh] bg-black flex flex-col relative overflow-hidden"
            onMouseMove={() => setShowControls(true)}
            data-testid="watch-container"
        >
            <Helmet>
                <title>{ogTitle} | SubHack</title>
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:type" content="video.other" />
                <meta property="og:site_name" content="SubHack" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={ogTitle} />
                <meta name="twitter:description" content={ogDescription} />
                <meta name="twitter:image" content={ogImage} />
            </Helmet>

            <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="absolute top-4 left-4 z-50 text-white hover:bg-white/20"
                data-testid="back-btn"
            >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
            </Button>

            {/* MAIN VIDEO AREA */}
            <div className="flex-1 relative flex items-center justify-center p-0 md:p-4 overflow-hidden">
                <video
                    ref={videoRef}
                    src={session?.clip?.video_url || ''}
                    className="w-full h-full object-contain cursor-pointer"
                    onClick={handlePlayPause}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={() => {
                        if (videoRef.current) {
                            setDuration(videoRef.current.duration);
                        }
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    muted={isMuted}
                    playsInline
                    data-testid="video-element"
                />

                <div className="absolute inset-0 scanline-overlay opacity-20 pointer-events-none" />

                {currentSub && (!showAdOverlay || session?.creator_is_pro || viewerIsPro) && !showExpiredOverlay && (
                    <div className="absolute bottom-20 left-0 right-0 flex justify-center px-4 z-20 pointer-events-none">
                        <span
                            className={`${subtitleStyle.fontClass} ${subtitleStyle.sizeClass} text-center max-w-[90%]`}
                            style={{
                                color: subtitleStyle.color,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                textShadow: '1px 1px 2px black',
                                boxDecorationBreak: 'clone',
                                WebkitBoxDecorationBreak: 'clone'
                            }}
                            dangerouslySetInnerHTML={{ __html: currentSub.text }}
                            data-testid="subtitle-overlay"
                        />
                    </div>
                )}

                {showUnmuteButton && (
                    <button
                        className="absolute top-4 right-4 z-50 bg-black/70 text-white px-4 py-2 rounded-full flex items-center gap-2 hover:bg-black/90 transition-colors"
                        onClick={handleUnmute}
                        data-testid="unmute-btn"
                    >
                        <VolumeX className="w-5 h-5" />
                        Click to Unmute
                    </button>
                )}

                {showWatermark && !showAdOverlay && !showExpiredOverlay && (
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
                        <span className="bg-primary text-primary-foreground text-[10px] md:text-xs font-heading uppercase tracking-widest px-2 py-1 rounded shadow-md border border-black/20 opacity-90">
                            Created with SubHack
                        </span>
                    </div>
                )}

                <div
                    className={`absolute bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-black/80 to-transparent px-4 pt-10 pb-6 md:pb-4 transition-opacity duration-300 ${showControls && (!showAdOverlay || session?.creator_is_pro || viewerIsPro) && !showExpiredOverlay
                            ? 'opacity-100'
                            : 'opacity-0'
                        }`}
                >
                    <div
                        className="w-full h-1 bg-white/20 rounded-full mb-3 cursor-pointer"
                        onClick={handleSeek}
                        data-testid="progress-bar"
                    >
                        <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-white hover:bg-white/20"
                                onClick={handlePlayPause}
                                data-testid="play-pause-btn"
                            >
                                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-white hover:bg-white/20"
                                onClick={() => setIsMuted(!isMuted)}
                            >
                                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                            </Button>
                            <span className="text-white text-sm font-mono">
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-white/60 text-sm flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                {session?.views || 0}
                            </span>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-white hover:bg-white/20"
                                onClick={handleFullscreen}
                            >
                                <Maximize2 className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                </div>

                {showAdOverlay && !isEmbedded && !session?.creator_is_pro && !viewerIsPro && (
                    <div className="absolute inset-0 bg-black/90 flex items-center justify-center z-50" data-testid="ad-overlay">
                        <div className="text-center max-w-md px-4">
                            <div className="mb-6">
                                <span className="font-brand text-4xl text-primary">SUBHACK</span>
                            </div>
                            <h2 className="font-heading text-xl uppercase mb-2">Support the Devs - Go Pro!</h2>
                            <p className="text-muted-foreground mb-6">
                                Limited Time Founder's Pricing Available - See Pricing page for details!
                            </p>
                            {adCountdown > 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    Skip in {adCountdown}s...
                                </p>
                            ) : (
                                <Button
                                    className="btn-primary"
                                    onClick={skipAd}
                                    data-testid="skip-ad-btn"
                                >
                                    Skip & Watch
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {showExpiredOverlay && (
                    <div className="absolute inset-0 bg-black/90 flex items-center justify-center z-50" data-testid="expired-overlay">
                        <div className="text-center max-w-md px-4">
                            <Clock className="w-16 h-16 text-destructive mx-auto mb-4" />
                            <h2 className="font-heading text-2xl uppercase mb-2">Link Expired</h2>
                            <p className="text-muted-foreground mb-6">
                                This link has expired. The creator must upgrade to Pro to restore it.
                            </p>
                            <div className="flex flex-col gap-2">
                                <Button
                                    className="btn-primary"
                                    onClick={() => navigate('/register')}
                                >
                                    Create Your Own
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => navigate('/')}
                                >
                                    Go Home
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom Bar (Fixed Height) */}
            <div className="h-16 shrink-0 border-t border-border bg-card/80 backdrop-blur flex items-center justify-between pl-4 pr-48 z-40 relative">

                {/* --- NEW: AVATAR & HANDLE SECTION --- */}
                <div className="flex items-center gap-4">
                    {/* Logo (Home Button) */}
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/')}
                        className="hover:bg-transparent p-0 hidden sm:flex" // Hide logo on tiny screens to save room for avatar
                    >
                        <img
                            src="/subhack_logo.svg"
                            alt="SubHack"
                            className="h-10 w-auto object-contain"
                        />
                    </Button>

                    {/* Separator (Desktop only) */}
                    <div className="hidden sm:block w-px h-8 bg-border/50"></div>

                    {/* Creator Profile */}
                    <div className="flex items-center gap-3">
                        {/* Avatar Circle */}
                        <div className={`relative w-10 h-10 rounded-full border p-[2px] bg-background flex-shrink-0 
                            ${session?.creator_plan_interval === 'year'
                                ? 'bg-gradient-to-tr from-[#FFD700] via-[#FFA500] to-[#FFD700] shadow-[0_0_8px_rgba(255,215,0,0.4)] border-transparent' // Annual Glow
                                : (session?.creator_is_pro ? 'border-primary' : 'border-border') // Standard Pro or Free
                            }`}>
                            <img
                                src={session?.creator_avatar_url || `https://api.dicebear.com/9.x/avataaars/svg?seed=${session?.creator_username || 'SubHack'}`}
                                alt={session?.creator_username}
                                className="w-full h-full rounded-full object-cover bg-muted border border-background"
                            />

                            {/* Pro Badge (Crown) */}
                            {session?.creator_is_pro && (
                                <div className={`absolute -top-1.5 -right-1.5 rounded-full flex items-center justify-center shadow-sm border border-background z-10
                                    ${session?.creator_plan_interval === 'year'
                                        ? 'bg-[#FFD700] text-black w-5 h-5'     // Annual: Gold Background, Black Icon
                                        : 'bg-[#FFFFFF] text-primary-foreground w-5 h-5' // Monthly: Brand Color, White Icon
                                    }`}>
                                    <Crown className="w-3 h-3 fill-current" />
                                </div>
                            )}
                        </div>

                        {/* Name & Title */}
                        <div className="flex flex-col justify-center">
                            <span className="font-heading text-lg leading-none text-foreground tracking-wide">
                                @{session?.creator_username || 'Anonymous'}
                            </span>
                            <span className="text-[10px] md:text-xs text-muted-foreground truncate max-w-[150px] md:max-w-[300px] leading-tight mt-0.5">
                                {session?.title}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/editor/${session?.clip_id}?remix=${shareId}`)}
                        className="gap-2"
                        title="Create a remix of this clip"
                        aria-label="Remix this clip"
                        data-testid="remix-btn"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <span className="hidden sm:inline">Remix</span>
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                            // 1. Get the backend base URL
                            const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

                            // 2. Extract the session ID from the end of the current URL
                            const sessionId = window.location.pathname.split('/').pop();

                            // 3. Build the Traffic Cop URL
                            const shareUrl = `${backendUrl}/api/share/${sessionId}`;

                            navigator.clipboard.writeText(shareUrl);
                            toast.success('Link copied!');
                        }}
                        title="Copy Link to Clipboard"
                        aria-label="Copy Link"
                        data-testid="share-btn"
                    >
                        <Share2 className="w-4 h-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => setShowReportDialog(true)}
                        title="Report this content"
                        aria-label="Report content"
                        data-testid="report-btn"
                    >
                        <Flag className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Report Dialog */}
            <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Report Content</DialogTitle>
                        <DialogDescription>
                            Help us keep SubHack safe. Reports are reviewed by our AI moderation system.
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        placeholder="Describe the issue..."
                        value={reportReason}
                        onChange={(e) => setReportReason(e.target.value)}
                        className="input-base"
                        rows={4}
                        data-testid="report-reason-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowReportDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleReport}
                            disabled={reporting}
                            data-testid="submit-report-btn"
                        >
                            {reporting ? 'Submitting...' : 'Submit Report'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}